import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, FileText, GitCompareArrows } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportVersionDiff } from "./ReportVersionDiff";
import { cn } from "@/lib/utils";

interface ReportVersionHistoryProps {
  reportId: string;
  currentVersion: number;
}

export const ReportVersionHistory = ({ reportId, currentVersion }: ReportVersionHistoryProps) => {
  const [versions, setVersions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedA, setSelectedA] = useState<string | null>(null);
  const [selectedB, setSelectedB] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [reportId]);

  const loadVersions = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("decision_report_versions")
        .select("*")
        .eq("report_id", reportId)
        .order("version_number", { ascending: false });

      if (error) throw error;
      setVersions(data || []);

      // Auto-select last two versions for diff
      if (data && data.length >= 2) {
        setSelectedA(data[1].id); // older
        setSelectedB(data[0].id); // newer
      }
    } catch (error) {
      console.error("Error loading versions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSelection = (versionId: string) => {
    if (selectedA === versionId) {
      setSelectedA(null);
    } else if (selectedB === versionId) {
      setSelectedB(null);
    } else if (!selectedA) {
      setSelectedA(versionId);
    } else if (!selectedB) {
      setSelectedB(versionId);
    } else {
      // Replace older selection
      setSelectedA(selectedB);
      setSelectedB(versionId);
    }
  };

  const versionA = versions.find((v) => v.id === selectedA);
  const versionB = versions.find((v) => v.id === selectedB);

  // Ensure A is always the older version
  const sortedPair = versionA && versionB
    ? versionA.version_number < versionB.version_number
      ? { a: versionA, b: versionB }
      : { a: versionB, b: versionA }
    : null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-6 w-6" />
            Versions-Historie
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-6 w-6" />
              Versions-Historie
            </CardTitle>
            <Badge variant="outline">Aktuelle Version: v{currentVersion}</Badge>
          </div>
          {versions.length >= 2 && (
            <p className="text-sm text-muted-foreground mt-1">
              Wähle zwei Versionen zum Vergleichen aus.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {versions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine früheren Versionen vorhanden</p>
              <p className="text-sm mt-1">Dies ist die erste Version des Reports</p>
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((version) => {
                const isSelected = selectedA === version.id || selectedB === version.id;
                const selectionLabel =
                  selectedA === version.id ? "A" : selectedB === version.id ? "B" : null;

                return (
                  <button
                    key={version.id}
                    onClick={() => handleToggleSelection(version.id)}
                    className={cn(
                      "w-full text-left p-4 rounded-lg border transition-colors",
                      isSelected
                        ? "border-primary/50 bg-primary/5"
                        : "bg-muted/50 border-border hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {selectionLabel && (
                            <Badge variant="default" className="text-[10px] px-1.5 py-0 h-5">
                              {selectionLabel}
                            </Badge>
                          )}
                          <Badge variant="outline">Version {version.version_number}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(version.created_at), {
                              addSuffix: true,
                              locale: de,
                            })}
                          </span>
                        </div>
                        {version.changes_summary && (
                          <p className="text-sm text-foreground/80">{version.changes_summary}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(version.created_at).toLocaleString("de-DE")}
                        </p>
                      </div>
                      <GitCompareArrows
                        className={cn(
                          "h-4 w-4 mt-1 flex-shrink-0 transition-colors",
                          isSelected ? "text-primary" : "text-muted-foreground/40"
                        )}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diff View */}
      {sortedPair && (
        <ReportVersionDiff versionA={sortedPair.a} versionB={sortedPair.b} />
      )}
    </div>
  );
};
